// What each state does for retirees beyond its brackets, standard deduction
// and personal exemption (those are in stateTaxRates.js, generated). Entered
// by hand from each state's own forms and instructions, read in October 2026:
// mostly the tax-year-2025 booklets, the latest published, with 2026 figures
// where a state had issued them. `basis` records which. Check a state against
// its revenue department before changing it, and keep `applied` and `leftOut`
// in step with the numbers: they are shown to the user.
//
// Amounts given as [single, married filing jointly]. "Each" means per person
// old enough; a couple's allowances are added together, as if each spouse had
// enough income of their own to use theirs.
//
// socialSecurity   absent: not taxed. Present: taxed to the federal extent, unless
//                  exemptBelow (federal AGI) with an optional phaseTo, needsAge
//                  (the income test only helps from this age), exemptFromAge,
//                  capShareOfBenefits, or credit (Utah).
// retirement       pension and retirement-account income left out of tax:
//                  all, or cap each from minAge (capFromAge: a different cap
//                  from an older age), or perReturn; lessSocialSecurity: the cap
//                  is reduced by benefits received; incomeLimit / incomeLimitEnd:
//                  phased out between two incomes (incomeLessSS: measured without
//                  taxable Social Security); steps: share allowed by federal AGI;
//                  noIra: IRA withdrawals do not count; pensionExempt: employer
//                  pensions are left out entirely; coversGains: investment gains
//                  share the same allowance.
// seniorDeduction  from 65, each: amount, or [single, married each];
//                  phase, lessIncomeOver (dollar for dollar), lessTaxableSS.
// seniorCredit     off the tax, each, from `age` (65 unless given).
// returnCredits    flat credits per return.
// federalSeniorDeduction  the state starts from federal taxable income, so the
//                  federal extra standard deduction at 65 carries through.
// gainsExclusion (share), gainsExclusionFlat, gainsRates, gainsMaxRate.
// federalTaxDeduction  federal income tax paid comes off state income.
// rates, deduction     replace the generated figure where the state changed it
//                  after the Tax Foundation table was published.

const TY25 = 'Tax year 2025 forms';

export const STATE_RETIREMENT_RULES = {
  AL: {
    basis: TY25,
    retirement: { pensionExempt: true, cap: 6000, minAge: 65 },
    federalTaxDeduction: {},
    deductionPhase: { start: [25500, 26000], end: [35500, 36000], floor: [2500, 5000] },
    applied: 'Social Security and employer pensions are not taxed; the first $6,000 of other retirement income each is left out from 65; federal income tax paid is deducted.',
    leftOut: 'local occupational taxes.',
    sources: ['https://www.revenue.alabama.gov/individual-corporate/income-exempt-from-alabama-income-taxation/'],
  },
  AR: {
    basis: TY25,
    retirement: { cap: 6000 },
    seniorCredit: { amount: 29 },
    gainsExclusion: 0.5,
    applied: 'Social Security is not taxed; the first $6,000 of pension and retirement-account income each is left out; half of long-term gains is left out; a $29 credit each from 65.',
    leftOut: 'the lower rate table for incomes under about $92,000 differs slightly from the one used.',
    sources: ['https://www.dfa.arkansas.gov/wp-content/uploads/2025_AR1000F_and_AR1000NR_Instructions.pdf'],
  },
  AZ: {
    basis: 'Tax year 2025 forms; standard deduction as set by HB 4168 (2026) for 2025',
    deduction: [15750, 31500],
    seniorDeduction: { amount: 2100 },
    gainsExclusion: 0.25,
    applied: 'Social Security is not taxed; pensions and retirement-account withdrawals are taxed in full; $2,100 exemption each from 65; a quarter of long-term gains is left out (assets bought since 2012).',
    leftOut: 'the new subtraction matching the federal senior deduction.',
    sources: ['https://www.azleg.gov/legtext/57leg/2R/summary/S.4168-1861ATT_ASENACTED.DOCX.htm'],
  },
  CA: {
    basis: TY25,
    seniorCredit: { amount: 153 },
    applied: 'Social Security is not taxed; pensions and retirement-account withdrawals are taxed in full; an extra $153 credit each from 65.',
    leftOut: 'the phase-out of exemption credits at high incomes.',
    sources: ['https://www.ftb.ca.gov/forms/2025/2025-540-booklet.html'],
  },
  CO: {
    basis: 'Department of Revenue guidance, January 2025',
    custom: 'CO',
    socialSecurity: {},
    federalSeniorDeduction: true,
    applied: 'From 65, Social Security is not taxed and up to $24,000 of pension and retirement-account income each is left out, less the Social Security left out. From 55 to 64 the allowance is $20,000 each, and Social Security is fully left out only below $75,000 of income ($95,000 for a couple). Under 55 both are taxed.',
    leftOut: 'A couple is treated as sharing income equally.',
    sources: ['https://tax.colorado.gov/sites/tax/files/documents/ITT_Social_Security_Pensions_and_Annuities_Jan_2025.pdf'],
  },
  CT: {
    basis: 'Office of Legislative Research report 2025-R-0152, for 2026',
    socialSecurity: { exemptBelow: [75000, 100000], capShareOfBenefits: 0.25 },
    retirement: {
      all: true,
      steps: [
        [[75000, 100000], 1], [[77500, 105000], 0.85], [[80000, 110000], 0.7], [[82500, 115000], 0.55], [[85000, 120000], 0.4],
        [[87500, 125000], 0.25], [[90000, 130000], 0.1], [[95000, 140000], 0.05], [[100000, 150000], 0.025],
      ],
    },
    exemptionPhase: { start: [30000, 48000], end: [45000, 72000] },
    applied: 'Below $75,000 of income ($100,000 for a couple) Social Security, pensions and retirement-account withdrawals are not taxed. Above that the share of pension and account income left out falls in steps to nothing at $100,000 ($150,000), and up to a quarter of Social Security is taxed.',
    leftOut: 'the phase-out of the 2% rate and the recapture charges at higher incomes, and the personal tax credit at low incomes.',
    sources: ['https://prdext2.cga.ct.gov/2025/rpt/pdf/2025-R-0152.pdf'],
  },
  DC: {
    basis: TY25,
    seniorDeduction: { amount: [2000, 1600] },
    applied: 'Social Security is not taxed; pensions and retirement-account withdrawals are taxed in full; a larger standard deduction from 65.',
    leftOut: '',
    sources: ['https://otr.cfo.dc.gov/sites/default/files/dc/sites/otr/publication/attachments/2025_D40_Book_082026_v1.pdf'],
  },
  DE: {
    basis: TY25,
    retirement: { cap: 12500, minAge: 60, coversGains: true },
    seniorDeduction: { amount: 2500 },
    seniorCredit: { amount: 110, age: 60 },
    applied: 'Social Security is not taxed; from 60, the first $12,500 each of pension, retirement-account and investment income is left out; $2,500 more standard deduction each from 65 and an extra $110 credit each from 60.',
    leftOut: 'the $2,000 pension exclusion before 60.',
    sources: ['https://revenuefiles.delaware.gov/2025/PITForms_Instructions/Instructions/PIT-RES_Instructions_2025-01.pdf'],
  },
  GA: {
    basis: 'Tax year 2025 forms; 2026 rate and standard deduction as reported for HB 463 (2026)',
    rates: { single: [[0.0499, 0]], married: [[0.0499, 0]] },
    deduction: [15000, 30000],
    retirement: { cap: 35000, minAge: 62, capFromAge: { age: 65, cap: 65000 }, coversGains: true },
    applied: 'Social Security is not taxed; pension, retirement-account and investment income is left out up to $35,000 each from 62 and $65,000 each from 65.',
    leftOut: '',
    sources: ['https://dor.georgia.gov/retirement-income-exclusion', 'https://dor.georgia.gov/document/document/2025-it-511-individual-income-tax-booklet/download'],
  },
  HI: {
    basis: TY25,
    retirement: { pensionExempt: true },
    seniorDeduction: { amount: 1144 },
    gainsMaxRate: 0.0725,
    applied: 'Social Security and employer-funded pensions are not taxed; withdrawals from 401(k)s and IRAs are taxed in full; an extra exemption each from 65; long-term gains are taxed at no more than 7.25%.',
    leftOut: 'the part of a 401(k) that came from employer contributions, which Hawaii also leaves out.',
    sources: ['https://files.hawaii.gov/tax/forms/2025/n11ins.pdf'],
  },
  IA: {
    basis: 'Department of Revenue guidance',
    retirement: { all: true, minAge: 55 },
    federalSeniorDeduction: true,
    applied: 'Social Security is not taxed; from 55, pensions and retirement-account withdrawals are not taxed either.',
    leftOut: 'local school and emergency surtaxes.',
    sources: ['https://revenue.iowa.gov/taxes/tax-guidance/individual-income-tax/retirement-income-tax-guidance'],
  },
  ID: {
    basis: 'State Tax Commission guidance',
    federalSeniorDeduction: true,
    applied: 'Social Security is not taxed; pensions and retirement-account withdrawals are taxed in full.',
    leftOut: 'the federal senior deduction, which Idaho follows, and the grocery credit.',
    sources: ['https://tax.idaho.gov/taxes/income-tax/individual-income/popular-credits-and-deductions/idaho-retirement-benefits-deduction'],
  },
  IL: {
    basis: TY25,
    retirement: { all: true },
    seniorDeduction: { amount: 1000 },
    exemptionPhase: { start: [250000, 500000], end: [250000, 500000] },
    applied: 'Social Security, pensions and retirement-account withdrawals (conversions too) are not taxed at any age; an extra $1,000 exemption each from 65.',
    leftOut: '',
    sources: ['https://tax.illinois.gov/research/publications/pubs/retirement-income.html'],
  },
  IN: {
    basis: TY25,
    seniorDeduction: { amount: 1000 },
    applied: 'Social Security is not taxed; pensions and retirement-account withdrawals are taxed in full; an extra $1,000 exemption each from 65.',
    leftOut: '',
    // Every county adds a flat rate on the same income; entered on Profile.
    localIncomeTax: { min: 0.005, max: 0.03, asOf: 'October 2026', what: 'county income tax, which every Indiana county charges on top' },
    sources: ['https://forms.in.gov/Download.aspx?id=16936', 'https://www.in.gov/dor/files/dn01.pdf'],
  },
  KS: {
    basis: 'Tax year 2025 booklet and Notice 24-08',
    seniorDeduction: { amount: [850, 700] },
    applied: 'Social Security is not taxed; private pensions and retirement-account withdrawals are taxed in full; a larger standard deduction from 65.',
    leftOut: '',
    sources: ['https://www.ksrevenue.gov/pdf/ip25.pdf', 'https://www.ksrevenue.gov/taxnotices/notice24-08.pdf'],
  },
  KY: {
    basis: 'Department of Revenue, 2026',
    retirement: { cap: 31110 },
    seniorCredit: { amount: 40 },
    deductionEachSpouse: true,
    applied: 'Social Security is not taxed; the first $31,110 of pension and retirement-account income each is left out; a $40 credit each from 65.',
    leftOut: 'city and county occupational taxes (they fall on pay, not retirement income).',
    sources: ['https://revenue.ky.gov/Individual/Individual-Income-Tax/Pages/default.aspx'],
  },
  LA: {
    basis: 'Department of Revenue guidance for 2025; the 2026 amount is indexed slightly higher',
    retirement: { cap: 12000, minAge: 65 },
    applied: 'Social Security is not taxed; from 65, the first $12,000 of pension and retirement-account income each is left out.',
    leftOut: '',
    sources: ['https://revenue.louisiana.gov/tax-education-and-faqs/faqs/income-tax-reform/did-the-annual-retirement-exemption-change/'],
  },
  MA: {
    basis: 'Tax year 2025',
    seniorDeduction: { amount: 700 },
    applied: 'Social Security is not taxed; private pensions and retirement-account withdrawals are taxed in full; an extra $700 exemption each from 65.',
    leftOut: '',
    sources: ['https://www.mass.gov/info-details/massachusetts-tax-rates'],
  },
  MD: {
    basis: 'Comptroller figures for 2026',
    retirement: { cap: 40600, minAge: 65, lessSocialSecurity: true, noIra: true },
    seniorDeduction: { amount: 1000 },
    applied: 'Social Security is not taxed; from 65, up to $40,600 each of pension and 401(k)-type income is left out, less the Social Security received; IRA withdrawals are taxed in full; an extra $1,000 exemption each from 65.',
    leftOut: 'the senior tax credit.',
    // Every county (and Baltimore City) adds a rate on Maryland taxable income; entered on Profile.
    // Anne Arundel and Frederick use tiers; one rate is an estimate there.
    localIncomeTax: { min: 0.0225, max: 0.033, asOf: '2026', what: 'county income tax, which every Maryland county charges on top and which can exceed the state tax' },
    sources: ['https://www.marylandcomptroller.gov/content/dam/mdcomp/tax/forms/2025/502r.pdf', 'https://dls.maryland.gov/pubs/prod/NoPblTabPDF/2026CountyLocalTaxRates.pdf'],
  },
  ME: {
    basis: 'Maine Revenue Services 2026 rate schedule; pension deduction at its 2025 amount',
    deduction: [15700, 31400],
    retirement: { cap: 48216, minAge: 55, lessSocialSecurity: true, incomeLimit: [125000, 250000], incomeLimitEnd: [225000, 350000] },
    seniorDeduction: { amount: [2050, 1650] },
    applied: 'Social Security is not taxed; up to $48,216 each of pension and retirement-account income is left out, less the Social Security received, phased out between $125,000 and $225,000 of income ($250,000 and $350,000 for a couple); a larger standard deduction from 65.',
    leftOut: 'the phase-out of the standard deduction at high incomes.',
    sources: ['https://legislature.maine.gov/statutes/36/title36sec5122.html', 'https://www.maine.gov/revenue/sites/maine.gov.revenue/files/2026-05/ind_tax_rate_sched_2026_rev.pdf'],
  },
  MI: {
    basis: 'Reported 2026 limits (Revenue Administrative Bulletin 2026-1); 2025 Form 4884',
    retirement: { perReturn: [67610, 135220] },
    applied: 'Social Security is not taxed; pension and retirement-account income is left out up to $67,610 a return ($135,220 for a couple).',
    leftOut: 'city income taxes (Detroit and about two dozen others), and the alternative $20,000 deduction from 67.',
    sources: ['https://taxsim.nber.org/historical_state_tax_forms/MI/2025/4884.pdf'],
  },
  MN: {
    basis: 'Department of Revenue inflation-adjusted amounts for 2026',
    socialSecurity: { exemptBelow: [86410, 110780], phaseTo: [126410, 150780] },
    seniorDeduction: { amount: [2000, 1600] },
    applied: 'Social Security is not taxed below $86,410 of income ($110,780 for a couple), and is taxed progressively more over the next $40,000; pensions and retirement-account withdrawals are taxed in full; a larger standard deduction from 65.',
    leftOut: 'the alternative Social Security subtraction, and the reduction of the standard deduction at high incomes.',
    sources: ['https://www.revenue.state.mn.us/sites/default/files/2025-12/inflation-adjusted-amounts-2026.pdf'],
  },
  MO: {
    basis: 'Department of Revenue guidance; 2025 Form MO-1040',
    socialSecurity: { exemptFromAge: 62 },
    retirement: { cap: 6000, incomeLimit: [25000, 32000], incomeLimitEnd: [31000, 44000], incomeLessSS: true },
    gainsExclusion: 1,
    federalSeniorDeduction: true,
    federalTaxDeduction: { cap: [5000, 10000], shares: [[25000, 0.35], [50000, 0.25], [100000, 0.15], [125000, 0.05]] },
    applied: 'Social Security is not taxed from 62; capital gains are not taxed; the $6,000 private pension exemption applies only at low incomes; part of federal income tax paid is deducted.',
    leftOut: 'Kansas City and St. Louis earnings taxes (they fall on pay).',
    sources: ['https://dor.mo.gov/faq/taxation/individual/pension.html', 'https://dor.mo.gov/faq/taxation/individual/capital-gains-subtraction.html'],
  },
  MS: {
    basis: 'Department of Revenue',
    retirement: { all: true, minAge: 60 },
    seniorDeduction: { amount: 1500 },
    applied: 'Social Security is not taxed; from 60, pensions and retirement-account withdrawals are not taxed either; an extra $1,500 exemption each from 65.',
    leftOut: '',
    sources: ['https://www.dor.ms.gov/individual/individual-income-tax-faqs', 'https://www.dor.ms.gov/individual/tax-rates'],
  },
  MT: {
    basis: 'Department of Revenue; the 65-and-over subtraction at its 2025 amount',
    socialSecurity: {},
    seniorDeduction: { amount: 5660 },
    federalSeniorDeduction: true,
    gainsRates: { under: 0.03, over: 0.041 },
    applied: 'Social Security is taxed to the extent it is taxed federally; pensions and retirement-account withdrawals are taxed in full; $5,660 is subtracted each from 65; long-term gains are taxed at 3% and 4.1%.',
    leftOut: '',
    sources: ['https://revenue.mt.gov/montana-tax-simplification-resource-hub', 'https://revenue.mt.gov/news/recent-news/HB-337'],
  },
  NC: {
    basis: TY25,
    applied: 'Social Security is not taxed; pensions and retirement-account withdrawals are taxed in full.',
    leftOut: '',
    sources: ['https://www.ncdor.gov/2025-d-401-individual-income-tax-instructions/open'],
  },
  ND: {
    basis: TY25,
    federalSeniorDeduction: true,
    gainsExclusion: 0.4,
    applied: 'Social Security is not taxed; pensions and retirement-account withdrawals are taxed in full; 40% of long-term gains is left out.',
    leftOut: '',
    sources: ['https://www.tax.nd.gov/sites/www/files/documents/forms/individual/2025-iit/2025-individual-income-tax-booklet.pdf'],
  },
  NE: {
    basis: 'Secondary sources citing the Department of Revenue',
    applied: 'Social Security is not taxed; pensions and retirement-account withdrawals are taxed in full.',
    leftOut: 'the larger standard deduction from 65.',
    sources: ['https://revenue.nebraska.gov'],
  },
  NJ: {
    basis: 'Division of Taxation, tax year 2025',
    custom: 'NJ',
    // New Jersey's brackets, exemptions and pension exclusion are set in statute and not indexed.
    bracketsIndexed: false,
    seniorDeduction: { amount: 1000 },
    applied: 'Social Security is not taxed; from 62, pension and retirement-account income is left out up to $75,000 ($100,000 for a couple) when total income is $100,000 or less, a part of it up to $150,000, and none above; an extra $1,000 exemption each from 65.',
    leftOut: 'the use of an unused exclusion against other income.',
    sources: ['https://www.nj.gov/treasury/taxation/njit7.shtml'],
  },
  NM: {
    basis: 'Tax year 2025',
    socialSecurity: { exemptBelow: [100000, 150000] },
    seniorDeduction: { amount: 8000, phase: { start: [18000, 30000], end: [28500, 51000] } },
    federalSeniorDeduction: true,
    gainsExclusionFlat: 2500,
    applied: 'Social Security is not taxed below $100,000 of income ($150,000 for a couple) and is taxed as it is federally above; pensions and retirement-account withdrawals are taxed in full; $2,500 of gains is left out; an exemption from 65 at low incomes.',
    leftOut: '',
    sources: ['https://taxsim.nber.org/historical_state_tax_forms/NM/2025/2025pit-adj.pdf'],
  },
  NY: {
    basis: 'Department of Taxation and Finance',
    retirement: { cap: 20000, minAge: 60 },
    applied: 'Social Security is not taxed; from 59 1/2, the first $20,000 of pension and retirement-account income each is left out.',
    leftOut: 'New York City and Yonkers income taxes, and the charge that takes back the benefit of lower brackets at high incomes.',
    sources: ['https://www.tax.ny.gov/pit/file/information_for_seniors.htm'],
  },
  OH: {
    basis: TY25,
    returnCredits: [
      { amount: 50, minAge: 65, incomeBelow: 100000 },
      { amount: 200, retirementOver: 8000, incomeBelow: 100000 },
    ],
    applied: 'Social Security is not taxed; pensions and retirement-account withdrawals are taxed in full, with a $200 retirement income credit and a $50 credit from 65 below $100,000 of income.',
    leftOut: 'city and school district income taxes (city taxes fall on pay, not retirement income).',
    sources: ['https://dam.assets.ohio.gov/image/upload/v1767095693/tax.ohio.gov/forms/ohio_individual/individual/2025/it1040-booklet.pdf'],
  },
  OK: {
    basis: TY25,
    retirement: { cap: 10000, minAge: 60 },
    applied: 'Social Security is not taxed; the first $10,000 of pension and retirement-account income each is left out.',
    leftOut: '',
    sources: ['https://oklahoma.gov/content/dam/ok/en/tax/documents/forms/individuals/current/511-Pkt.pdf'],
  },
  OR: {
    basis: 'Publication OR-17 for 2025',
    seniorDeduction: { amount: [1200, 1000] },
    exemptionPhase: { start: [100000, 200000], end: [100000, 200000] },
    federalTaxDeduction: { cap: [8500, 8500], phase: { start: [125000, 250000], end: [145000, 290000] } },
    applied: 'Social Security is not taxed; pensions and retirement-account withdrawals are taxed in full; a larger standard deduction from 65; up to $8,500 of federal income tax paid is deducted below $125,000 of income ($250,000 for a couple).',
    leftOut: 'Portland-area income taxes on high incomes, and the retirement income credit for low incomes.',
    sources: ['https://www.oregon.gov/dor/forms/FormsPubs/publication-or-17_101-431_2025.pdf'],
  },
  PA: {
    basis: TY25,
    retirement: { all: true, minAge: 60 },
    applied: 'Social Security is not taxed; from 59 1/2, pensions and retirement-account withdrawals are not taxed either. Earlier withdrawals are taxed.',
    leftOut: 'local earned income taxes (they fall on pay), and the recovery of your own contributions before an early withdrawal is taxed.',
    sources: ['https://www.pa.gov/content/dam/copapwp-pagov/en/revenue/documents/formsandpublications/formsforindividuals/pit/documents/2025/2025_pa-40in.pdf'],
  },
  RI: {
    basis: 'Division of Taxation Advisory 2025-22 (2025 limits) and Publication 2026-01',
    socialSecurity: { exemptBelow: [107000, 133750], needsAge: 67 },
    retirement: { cap: 50000, minAge: 67, noIra: true, incomeLimit: [107000, 133750] },
    applied: 'From full retirement age and below $107,000 of income ($133,750 for a couple), Social Security is not taxed and up to $50,000 each of pension and 401(k)-type income is left out. Otherwise both are taxed, and IRA withdrawals always are.',
    leftOut: 'the phase-out of the standard deduction and exemptions above about $261,000.',
    sources: ['https://tax.ri.gov/sites/g/files/xkgbur541/files/2025-11/ADV_2025_22_Inflation_Adjustments.pdf', 'https://tax.ri.gov/sites/g/files/xkgbur541/files/2026-02/PUB_2026-01_Retirement_Income_Guide.pdf'],
  },
  SC: {
    basis: TY25,
    // From 65 the $15,000 deduction against any income takes the place of the retirement deduction.
    retirement: { cap: 3000, minAge: 60, capFromAge: { age: 65, cap: 0 } },
    seniorDeduction: { amount: 15000 },
    federalSeniorDeduction: true,
    gainsExclusion: 0.44,
    applied: 'Social Security is not taxed; $3,000 of pension and retirement-account income each is left out before 65, and $15,000 of any income each from 65; 44% of long-term gains is left out.',
    leftOut: '',
    sources: ['https://dor.sc.gov/sites/dor/files/forms/SC1040Instr_2025.pdf'],
  },
  UT: {
    basis: 'State Tax Commission, tax year 2025',
    socialSecurity: { credit: { rate: 0.025, start: [54000, 90000] } },
    deductionCreditPhase: { start: [18213, 36426], rate: 0.013 },
    applied: 'Social Security is taxed as it is federally, with a credit that cancels the tax below $54,000 of income ($90,000 for a couple) and shrinks above; pensions and retirement-account withdrawals are taxed in full; the taxpayer credit that stands in for a standard deduction shrinks as income rises.',
    leftOut: 'the retirement credit for those born before 1953.',
    sources: ['https://incometax.utah.gov/credits/ss-benefits', 'https://incometax.utah.gov/credits/taxpayer-tax-credit'],
  },
  VA: {
    basis: TY25,
    seniorDeduction: { amount: 12000, lessIncomeOver: [50000, 75000] },
    seniorExemption: 800,
    applied: 'Social Security is not taxed; pensions and retirement-account withdrawals are taxed in full; from 65, a $12,000 deduction each that shrinks dollar for dollar above $50,000 of income ($75,000 for a couple), and an extra $800 exemption each.',
    leftOut: '',
    sources: ['https://www.tax.virginia.gov/sites/default/files/vatax-pdf/2025-760-instructions.pdf'],
  },
  VT: {
    basis: 'Department of Taxes, tax year 2025',
    socialSecurity: { exemptBelow: [55000, 70000], phaseTo: [65000, 80000] },
    seniorDeduction: { amount: 1250 },
    gainsExclusionFlat: 5000,
    applied: 'Social Security is not taxed below $55,000 of income ($70,000 for a couple) and is taxed as it is federally above $65,000 ($80,000); pensions and retirement-account withdrawals are taxed in full; $5,000 of gains is left out; a larger standard deduction from 65.',
    leftOut: 'the minimum tax of 3% of income above $150,000.',
    sources: ['https://tax.vermont.gov/node/5363', 'https://tax.vermont.gov/sites/tax/files/documents/IN-153-Instr-2025.pdf'],
  },
  WA: {
    basis: 'Department of Revenue; 2025 deduction',
    rates: { single: [[0.07, 0], [0.099, 1000000]], married: [[0.07, 0], [0.099, 1000000]] },
    applied: 'No tax on pay, pensions, retirement accounts or Social Security. Long-term gains above $278,000 in a year are taxed at 7%, and at 9.9% above a further $1 million.',
    leftOut: '',
    sources: ['https://dor.wa.gov/taxes-rates/other-taxes/capital-gains-tax'],
  },
  WI: {
    basis: TY25,
    retirement: { cap: 24000, minAge: 67 },
    seniorDeduction: { amount: 250 },
    gainsExclusion: 0.3,
    deductionPhase: { start: [20119, 29039], end: [136453, 159690] },
    applied: 'Social Security is not taxed; from 67, the first $24,000 of pension and retirement-account income each is left out; 30% of long-term gains is left out; the standard deduction shrinks as income rises; an extra $250 exemption each from 65.',
    leftOut: 'Wisconsin credits, which a return using the retirement exclusion gives up.',
    sources: ['https://www.revenue.wi.gov/DOR%20Publications/pb106.pdf', 'https://www.revenue.wi.gov/TaxForms2025/2025-ScheduleSB-Inst.pdf'],
  },
  WV: {
    basis: 'Tax year 2025 forms; Social Security fully exempt from 2026 by statute',
    seniorDeduction: { amount: 8000, lessTaxableSS: true },
    applied: 'Social Security is not taxed; pensions and retirement-account withdrawals are taxed in full; from 65, $8,000 of income each is left out, less the Social Security already left out.',
    leftOut: '',
    sources: ['https://tax.wv.gov/Documents/PIT/2025/it140.PersonalIncomeTaxFormsAndInstructions.2025.pdf'],
  },
};
